/**
 * Google Sheets URL → CSV export URL 変換 (Sprint BC、GAP-A2)。
 *
 * 旧 v1 admin の Google Sheets 取込 flow を v2 で復活。
 *
 * 受け付ける URL 形式:
 *   1. `https://docs.google.com/spreadsheets/d/<id>/edit#gid=<gid>` (一般的なシェア URL)
 *   2. `https://docs.google.com/spreadsheets/d/<id>/edit?gid=<gid>`
 *   3. `https://docs.google.com/spreadsheets/d/<id>/edit?usp=sharing` (gid なし、最初のシート)
 *   4. すでに `/export?format=csv` 形式の URL (素通し)
 *   5. publish-to-web `https://docs.google.com/spreadsheets/d/e/<pub-id>/pub?...` (Sprint BC では非対応)
 *
 * 出力: `https://docs.google.com/spreadsheets/d/<id>/export?format=csv&gid=<gid>` (gid 取得失敗時は 0)。
 *
 * 注: 取得対象 sheet は admin の Google アカウントで「リンクを知っている全員に公開」
 * または「公開ウェブ」設定済が前提。失敗時の fallback は別 sprint。
 */

export class SheetsUrlError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SheetsUrlError';
  }
}

const SPREADSHEETS_PATH_REGEX = /\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/;

/**
 * URL から sheet id を抽出。
 */
export function extractSheetId(url: string): string | null {
  const m = SPREADSHEETS_PATH_REGEX.exec(url);
  return m?.[1] ?? null;
}

/**
 * URL から gid を抽出 (search params or hash fragment)。
 * gid が見つからなければ null。
 */
export function extractGid(url: string): string | null {
  // hash fragment (#gid=123)
  const hashIdx = url.indexOf('#');
  if (hashIdx >= 0) {
    const hash = url.slice(hashIdx + 1);
    const params = new URLSearchParams(hash);
    const gid = params.get('gid');
    if (gid) return gid;
  }
  // query string (?gid=123)
  const qIdx = url.indexOf('?');
  if (qIdx >= 0) {
    const q = url.slice(qIdx + 1, hashIdx >= 0 ? hashIdx : undefined);
    const params = new URLSearchParams(q);
    const gid = params.get('gid');
    if (gid) return gid;
  }
  return null;
}

/**
 * 任意の Google Sheets URL → CSV export URL に正規化。
 * 既に export?format=csv 形式ならそのまま返す。
 *
 * @throws SheetsUrlError - sheet id を抽出できなかった場合
 */
export function sheetsUrlToCsvUrl(rawUrl: string): string {
  const url = rawUrl.trim();
  if (!url) {
    throw new SheetsUrlError('URL が空です');
  }
  if (url.includes('/export?') && url.includes('format=csv')) {
    return url;
  }
  const id = extractSheetId(url);
  if (!id) {
    throw new SheetsUrlError(
      'Google Sheets URL の形式が認識できません (例: https://docs.google.com/spreadsheets/d/<id>/edit#gid=0)',
    );
  }
  const gid = extractGid(url) ?? '0';
  return `https://docs.google.com/spreadsheets/d/${id}/export?format=csv&gid=${gid}`;
}
